import React, { useState } from 'react';
import { Form, InputNumber, Button, Table, Modal, DatePicker, Alert, Typography } from 'antd';
import moment from 'moment';

const { Text } = Typography;

const MortgageOverpayments = ({
  monthlyOverpayment = 0,
  lumpSumOverpayments = [], // Provide default empty array
  onMonthlyOverpaymentChange,
  onLumpSumOverpaymentAdd,
  onLumpSumOverpaymentRemove,
  remainingBalance = 0,
  startDate = new Date(),
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newLumpSum, setNewLumpSum] = useState('');
  const [newLumpSumDate, setNewLumpSumDate] = useState(null);

  // Calculate 10% of remaining balance for warning
  const annualOverpaymentLimit = remainingBalance * 0.1;
  const totalPlannedOverpayments = (monthlyOverpayment * 12) + 
    (Array.isArray(lumpSumOverpayments) ? lumpSumOverpayments.reduce((sum, payment) => sum + payment.amount, 0) : 0);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => moment(date).format('MMMM YYYY'),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `£${amount.toLocaleString()}`,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button type="link" onClick={() => onLumpSumOverpaymentRemove(record.key)}>
          Remove
        </Button>
      ),
    },
  ];

  const handleAddLumpSum = () => {
    if (newLumpSum && newLumpSumDate) {
      onLumpSumOverpaymentAdd({
        key: Date.now(),
        date: newLumpSumDate.toDate(),
        amount: parseFloat(newLumpSum),
      });
      setNewLumpSum('');
      setNewLumpSumDate(null);
      setIsModalVisible(false);
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Form.Item 
        label="Monthly Overpayment"
        tooltip="Regular additional payment on top of required monthly payment"
      >
        <InputNumber
          value={monthlyOverpayment}
          onChange={onMonthlyOverpaymentChange}
          prefix="£"
          step={10}
          min={0}
          style={{ width: '100%' }}
        />
      </Form.Item>

      {totalPlannedOverpayments > annualOverpaymentLimit && (
        <Alert
          message="Overpayment Limit Warning"
          description={`Your planned overpayments (£${totalPlannedOverpayments.toLocaleString()}) exceed the typical annual limit of £${annualOverpaymentLimit.toLocaleString()} (10% of remaining balance). Check your mortgage terms.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <Button type="primary" onClick={() => setIsModalVisible(true)}>
          Add Lump Sum Overpayment
        </Button>
      </div>

      {lumpSumOverpayments.length > 0 && (
        <Table
          columns={columns}
          dataSource={lumpSumOverpayments}
          pagination={false}
          style={{ marginBottom: 16 }}
        />
      )}

      <Modal
        title="Add Lump Sum Overpayment"
        open={isModalVisible}
        onOk={handleAddLumpSum}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form layout="vertical">
          <Form.Item label="Amount">
            <InputNumber
              value={newLumpSum}
              onChange={setNewLumpSum}
              prefix="£"
              style={{ width: '100%' }}
              min={0}
            />
          </Form.Item>
          <Form.Item label="Date">
            <DatePicker
              value={newLumpSumDate}
              onChange={setNewLumpSumDate}
              style={{ width: '100%' }}
              disabledDate={(current) => {
                return current && current < moment(startDate);
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MortgageOverpayments;